import _ from 'lodash'
import dayjs from 'dayjs'
import axios from 'axios'
import { EntityState } from '@reduxjs/toolkit'
import { CommonState, Model } from '@/core/classes/Model'
import { Consultation } from './consultation.model'
import { Document } from './document.model'
import { AddressInfo, ContactInfo, SavedAddress, UserInfo } from '@/core/types/shared'
import { getEnv } from '@/utils/helpers'
import { ExternalAccountProvider, PatientExternalAccounts } from './staff.model'
import { Account } from './account.model'
import { SearchPatientsFilterParams, SearchRequestOptions } from '../types/request'
import { SearchResponse } from '../types/response'

type AttendingPhysician = AddressInfo & {
  firstname: string
  lastname: string
  email?: string
  phone?: string
}

export enum PatientIdentityStatus {
  PROVISIONAL = 'PROVISIONAL',
  RECOVERED = 'RECOVERED',
  VALIDATED = 'VALIDATED',
  QUALIFIED = 'QUALIFIED',
}

export type Patient = Omit<Account, 'userInfo'> & {
  /**
   * custom ID
   */
  id?: string
  account?: string
  agreement: string
  secu: string
  /** SEHA (en-SA): 'saudi' | 'non-saudi' */
  nationality?: string
  allergy?: string
  vaccins?: string
  background?: string
  treatment?: string
  notes?: string
  codeGestion?: string
  codeRegime?: string
  caisse?: string
  imc?: string
  height?: number
  weight?: number
  consultations: (Consultation | string)[]
  parents: Parent[]
  attendingPhysician?: AttendingPhysician
  files?: Document[]
  savedAddresses?: SavedAddress[]
  pluginId?: string
  externalAccounts?: PatientExternalAccounts
  netsoinsId?: string
  identityConfirmed?: boolean
  insNIR?: string
  insOID?: string
  userInfo: PatientInfo
  insHistory?: Array<{
    value: string
    key: string
    oid: string
    startDate: string
    endDate: string
  }>
  insured?: string
  insuredRelationship?: string
  insuranceProvider?: string
  primaryInsurance?: string
  secondaryInsurance?: string
  primaryPolicy?: string
  secondaryPolicy?: string
  primaryPharmacy?: string
}

type PatientInfo = UserInfo & {
  birthPlaceCode?: string
  birthName?: string
  birthSurname?: string
  primaryName?: string
}

export type CreatePatientDto = Omit<
  Patient,
  | '_id'
  | 'accountType'
  | 'active'
  | 'consultations'
  | 'createdAt'
  | 'email'
  | 'externalAccounts'
  | 'licenses'
  | 'parents'
  | 'permissions'
  | 'policies'
  | 'roles'
  | 'secu'
  | 'subscription'
  | 'type'
  | 'updatedAt'
> & {
  active?: boolean
  email?: string
  parents?: Omit<Parent, '_id' | 'patient'>[]
  secu?: string
}

export type UpdatePatientDto = Omit<
  {
    [k in keyof Patient]?: Patient[k] | null
  },
  'attendingPhysician' | 'contactInfo' | 'parents' | 'userInfo'
> & {
  attendingPhysician?:
  | {
    [k in keyof AttendingPhysician]?: AttendingPhysician[k] | null
  }
  | null
  contactInfo?: {
    [k in keyof ContactInfo]?: ContactInfo[k] | null
  }
  parents?: Array<{
    [k in keyof Omit<Parent, '_id' | 'patient'>]?: Omit<Parent, '_id' | 'patient'>[k] | null
  }>
  userInfo?: {
    [k in keyof PatientInfo]?: PatientInfo[k] | null
  }
}

export type Parent = AddressInfo & {
  _id: string
  email?: string
  firstname: string
  lastname: string
  phone?: string
  patient: string | Patient
}

export type PatientState = CommonState & {
  patients: EntityState<Patient, Patient['_id']>
  consents: Record<string, string | undefined>
  carteVitaleInfos: Array<Record<string, string>>
}

class PatientModel extends Model {
  static instance: PatientModel

  static getInstance() {
    if (!PatientModel.instance) {
      PatientModel.instance = new PatientModel('patient')
    }
    return PatientModel.instance
  }

  getInitialState(): PatientState {
    return _.merge({}, this.commonState, {
      requestTracker: {
        downloadConsent: {
          error: null,
          loading: false,
        },
      },
      patients: {
        entities: {},
        ids: [],
      },
      consents: {},
      carteVitaleInfos: [],
    })
  }

  getCarteVitaleInfos = async (): Promise<Array<Record<string, string>>> => {
    const carteVitaleConnectorUrl = getEnv('VITE_CARTE_VITALE_CONNECTOR_URL') || 'http://localhost:4001'
    const response = await axios.get(carteVitaleConnectorUrl + (carteVitaleConnectorUrl.startsWith('/') ? '' : '/') + 'api/devices/cartevitale')
    return response.data
  }

  createPatient = async (data: CreatePatientDto, token?: string): Promise<Patient> => {
    return await this.httpClient.request<Patient>('/patients', 'POST', data, token)
  }

  getPatient = async (id: string) => {
    return await this.httpClient.request<Patient>(`/patients/${id}`, 'GET', undefined)
  }

  getPatients = async (params?: SearchPatientsFilterParams): Promise<SearchResponse<Patient>> => {
    return await this.httpClient.request<SearchResponse<Patient>>('/patients/search', 'POST', {
      limit: 5, skip: 0, order: 'ASC', timezone: dayjs.tz.guess(), ...params,
    })
  }

  updatePatient = async (id: string, updatePatientDto: UpdatePatientDto): Promise<Patient> => {
    return await this.httpClient.request<Patient>(`/patients/${id}`, 'PATCH', updatePatientDto)
  }

  deletePatient = async (id: string) => {
    await this.httpClient.request(`/patients/${id}`, 'DELETE')
  }

  downloadConsent = async (id: string): Promise<string> => {
    return await this.httpClient.request<string>(`/patients/${id}/consent/download`, 'GET')
  }

  createPatientAccount = async (id: string) => {
    return await this.httpClient.request<Patient>(`/patients/${id}/account`, 'POST')
  }

  getOrCreateOrdoclicPatient = async (id: string) => {
    return await this.httpClient.request<Patient>(`/patients/${id}/ordoclic`, 'GET')
  }

  getOrCreateDesmosPatient = async (id: string) => {
    return await this.httpClient.request<Patient>(`/patients/${id}/desmos`, 'GET')
  }

  getPatientConsultations = async (patientId: string, options?: SearchRequestOptions): Promise<Consultation[]> => {
    return await this.httpClient.request<Consultation[]>(`/patients/${patientId}/consultations`, 'POST', options)
  }
}

export default PatientModel.getInstance()
